import React, { useState, useCallback } from "react";
import { blue, orange, red, yellow } from "@mui/material/colors";
import {
  Container,
  Button,
  Typography,
  Chip,
  alpha,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { formatCurrency } from "../../../utils/Format.js";
import styles from "./deliverersDetailsHeaderStyles.js";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TimelineIcon from "@mui/icons-material/Timeline";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import DeliverersConfirmationDialog from "../../../components/Deliverers/DeliverersConfirmationDialog";
import DeliverersDeliveredTable from "../DeliverersDeliveredTable/index.js";
import DeliverersReturnsTable from "../DeliverersReturnsTable/index.js";
import DeliverersReturnsMatrixTable from "../DeliverersReturnsMatrixTable/index.js";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

const DeliverersDetailsHeader = ({ deliveryMan, isLoading, getDeliverersDetails }) => {

  const { hasAllPermissions } = usePermissions();
  const [selectedTab, setSelectedTab] = useState("1");
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [correctAmount, setCorrectAmount] = useState(false);

  const [openDialog, setOpenDialog] = useState(null);

  const handleChange = useCallback((event, newValue) => {
    setSelectedTab(newValue);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(null);
  }, []);

  const getEffectivenessColor = useCallback((value) => {
    if (value >= 95 && value <= 100) {
      return blue[500];
    } else if (value >= 90 && value <= 94) {
      return "#53CA98";
    } else if (value >= 80 && value <= 89) {
      return yellow[700];
    } else if (value >= 70 && value <= 79) {
      return orange[500];
    } else {
      return red[500];
    }
  }, []);

  const deliveredList = deliveryMan?.delivered_guides_list?.map(guide => guide.id_guide) || [];
  const [selectionReturnsModel, setSelectionReturnsModel] = useState([]);
  const [selectionReturnsMatrixModel, setSelectionReturnsMatrixModel] = useState([]);

  const handleDialogOpen = useCallback((dialogName) => {
    setOpenDialog(dialogName);
  }, []);

  /* useEffect(() => {
    setCorrectAmount(deliveredList.length > 0);
  }, [deliveredList]);

  useEffect(() => {
    setCorrectAmount(deliveredList.length > 0 || selectionReturnsModel.length > 0 || selectionReturnsMatrixModel.length > 0);
  }, [deliveredList, selectionReturnsModel, selectionReturnsMatrixModel]); */

  return (
    <Container style={{ padding: 0 }}>
      <TabContext value={selectedTab}>
        <Container sx={styles.mainContainer}>
          {isLoading ? (
            <Container sx={styles.skeletonPrincipalContainer}>
              <Container sx={styles.skeletonMainContainer}>
                <Skeleton variant="circular" width={60} height={60} />
                <Container sx={styles.skeletonSecondaryContainer}>
                  <Skeleton variant="rounded" width={260} height={30} />
                  <Container sx={styles.skeletonTertiaryContainer}>
                    <Skeleton variant="rounded" width={100} height={20} />
                    <Skeleton variant="rounded" width={100} height={20} />
                  </Container>
                </Container>
              </Container>
              <Container sx={styles.skeletonTertiaryContainer}>
                <Skeleton variant="rounded" width={"33%"} height={50} />
                <Skeleton variant="rounded" width={"33%"} height={50} />
                <Skeleton variant="rounded" width={"33%"} height={50} />
              </Container>
            </Container>
          ) : (
            <Container style={styles.mainContainer}>
              <Container sx={{
                  ...styles.delivererDetailsContainer,
                  padding: isMobile ? "0px 20px !important" : "0px 0px !important",
                }}>
                <Container sx={styles.delivererDetailsInfoContainer}>
                  <Box sx={styles.circleAvatar}>
                    <AccountCircleIcon sx={styles.iconCircleAvatar} />
                  </Box>
                  <Box>
                    <Typography sx={styles.title}>
                      {deliveryMan?.name_complete}
                    </Typography>
                    <Box>
                      <Chip
                        sx={{
                          ...styles.statsChip,
                          color: getEffectivenessColor(deliveryMan?.effectiveness_percent),
                          backgroundColor: alpha(
                            getEffectivenessColor(deliveryMan?.effectiveness_percent),
                            0.1
                          ),
                        }}
                        icon={
                          <TimelineIcon
                            style={{
                              ...styles.statsIcon,
                              color: getEffectivenessColor(deliveryMan?.effectiveness_percent),
                            }}
                          />
                        }
                        label={(deliveryMan?.effectiveness_percent ? deliveryMan?.effectiveness_percent : 0) + "% de efectividad"}
                        size="small"
                      />
                      <Chip
                        icon={
                          <AccountBalanceWalletIcon
                            style={
                              deliveryMan?.balance < 0
                                ? { ...styles.moneyIcon, color: "#DB463C" }
                                : styles.moneyIcon
                            }
                            size="small"
                          />
                        }
                        sx={{
                          ...styles.moneyChip,
                          color:
                            deliveryMan?.balance < 0 ? "#DB463C" : "#53CA98",
                          backgroundColor:
                            deliveryMan?.balance < 0
                              ? alpha("#DB463C", 0.1)
                              : alpha("#53CA98", 0.1),
                        }}
                        label={formatCurrency(deliveryMan?.balance)}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Container>

                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {hasAllPermissions(["cut_cash_cls", 'cut_vw', 'cut_ovw']) && (<Button
                    variant="contained"
                    startIcon={<ContentCutIcon />}
                    onClick={() =>
                      handleDialogOpen("DeliverersConfirmationDialog")
                    }
                    sx={true ? styles.activeButton : styles.inactiveButton}
                  >
                    Realizar corte
                  </Button>)}
                </Box>
              </Container>

              <Box
                sx={{
                  display: { xs: "block", md: "none" },
                  marginTop: "8px",
                  padding: isMobile ? "0px 20px !important" : "0px 0px !important",
                }}
              >
                {hasAllPermissions(["cut_cash_cls", 'cut_vw', 'cut_ovw']) && (<Button
                  variant="contained"
                  disabled={false}
                  startIcon={<ContentCutIcon />}
                  onClick={() =>
                    {
                      setCorrectAmount(deliveredList.length > 0 || selectionReturnsModel.length > 0 || selectionReturnsMatrixModel.length > 0)
                      handleDialogOpen("DeliverersConfirmationDialog")
                    }
                  }
                  sx={
                    true
                      ? {
                          ...styles.activeButton,
                          display: "flex",
                          width: "100%",
                        }
                      : {
                          ...styles.inactiveButton,
                          display: "flex",
                          width: "100%",
                        }
                  }
                >
                  Realizar corte
                </Button>)}
              </Box>

              <Box style={styles.tabContainer}>
                <TabList
                  onChange={handleChange}
                  variant={isMobile ? "scrollable" : "fullWidth"}
                  scrollButtons={isMobile ? "auto" : "off"}
                  aria-label="responsive tabs"
                >
                  <Tab
                    icon={
                      <Typography style={styles.badge}>
                        {deliveryMan?.delivered_guides_list.length}
                      </Typography>
                    }
                    iconPosition="start"
                    label="Entregadas"
                    value="1"
                  />
                  <Tab
                    icon={
                      <Typography style={styles.badge}>
                        {deliveryMan?.failed_delivered_guides_list.length}
                      </Typography>
                    }
                    iconPosition="start"
                    label="Fallidas"
                    value="2"
                  />
                  <Tab
                    icon={
                      <Typography style={styles.badge}>
                        {deliveryMan?.pending_guide_list.length}
                      </Typography>
                    }
                    iconPosition="start"
                    label="Pendientes de devolución"
                    value="3"
                  />
                </TabList>
              </Box>
            </Container>
          )}
        </Container>

        <Container
          style={styles.mainContainer}
          sx={{ padding: "0 !important" }}
        >
          <Box sx={{ width: "100%" }}>
            <TabPanel value="1">
              <DeliverersDeliveredTable
                guides={deliveryMan?.delivered_guides_list}
                isLoading={isLoading}
              />
            </TabPanel>
            <TabPanel value="2">
              <DeliverersReturnsTable
                guides={deliveryMan?.failed_delivered_guides_list}
                isLoading={isLoading}
                selectionReturnsModel={selectionReturnsModel}
                setSelectionReturnsModel={setSelectionReturnsModel}
              />
            </TabPanel>
            <TabPanel value="3">
              <DeliverersReturnsMatrixTable
                guides={deliveryMan?.pending_guide_list}
                isLoading={isLoading}
                selectionReturnsMatrixModel={selectionReturnsMatrixModel}
                setSelectionReturnsMatrixModel={setSelectionReturnsMatrixModel}
              />
            </TabPanel>
          </Box>
        </Container>

        <DeliverersConfirmationDialog
          open={openDialog === "DeliverersConfirmationDialog"}
          onClose={handleDialogClose}
          deliveryMan={deliveryMan}
          getDeliverersDetails={getDeliverersDetails}
          totalDeliveredGuides={deliveredList}
          totalReturnGuides={selectionReturnsModel}
          totalReturnMatrixGuides={selectionReturnsMatrixModel}
          correctAmount={correctAmount}
          setCorrectAmount={setCorrectAmount}
        />
      </TabContext>
    </Container>
  );
};

export default DeliverersDetailsHeader;
